import appManager from '@app/appManager';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import AppModule from '@app/lib/AppModule';
import {
  ITask,
  TaskActionOptions,
  TaskState,
} from '@modules/TasksModule/types';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';
import {
  ServiceSessionCreatedEvent,
  ServiceSessionEvent,
  ServiceSessionEventType,
  ServiceSessionOfferedEvent,
  ServiceSessionUserEvent,
  ServiceSessionRecoveryEvent,
} from '@modules/ServiceModule/workspace/types/ServiceEvents';
import handleSessionDelivered from './events/handleSessionDelivered';
import handleSessionDeleted from './events/handleSessionDeleted';
import handleSessionCreated from './events/handleSessionCreated';
import handleSessionRecoveryStarted from './events/handleSessionRecoveryStarted';
import handleSessionRecoveryCompleted from './events/handleSessionRecoveryCompleted';
import handleSessionRescinded from './events/handleSessionRescinded';
import handleSessionRejected from './events/handleSessionRejected';
import handleSessionHangup from './events/handleSessionHangup';
import {
  CallEvent,
  PhonePrepareOutgoingEvent,
  WebphoneCallEvent,
} from '@modules/SoftPhoneModule/types';
import handleCallAnswered from './events/handleCallAnswered';
import handleCallMissed from './events/handleCallMissed';
import handleCallEnded from './events/handleCallEnded';
import { inboxState, inboxActions, resetInboxState } from './state';
import type {
  ClassificationRequirement,
  DeferredSelection,
} from '@modules/TasksModule/types';

/** Answer of `canCloseTask`; `requirement` is set only when the check ran. */
export type CloseCheck = {
  status: 'ready' | 'blocked';
  requirement?: ClassificationRequirement;
};
import { InboxContext } from './types';
import { applyDeferredSelection } from './lib/applyDeferredSelection';
import { isBookingRetentionEnabled } from './lib/isBookingRetentionEnabled';
import { isNewAssignedToMeUiEnabled } from './lib/isNewAssignedToMeUiEnabled';
import handleTaskAccept from './events/handleTaskAccept';
import handleTaskReject from './events/handleTaskReject';
import handleTaskComplete from './events/handleTaskComplete';
import handleTaskPrepareOutbound from './events/handleTaskPrepare';
import handleTaskUnhold from './events/handleTaskUnhold';
import handleCallFailed from './events/handleCallFailed';
import handleCallFailedGeneral from './events/handleCallFailedGeneral';
import handleTaskTransfer from './events/handleTaskTransfer';

import handleSessionOffered from './events/handleSessionOffered';
import handleCallPlaced from './events/handleCallPlaced';
import TasksModule from '@modules/TasksModule';
import ServiceSession from '@modules/ServiceModule/workspace/types/ServiceSession';
import { tasksState } from '@modules/TasksModule/state';
import {
  CallRecordingSettingsOptions,
  ChannelClassificationInput,
  ConversationTrait,
} from '@api/generated/graphql';
import Task from '@modules/TasksModule/Task';
import searchCustomers from '@components/CustomerSearch/data/searchCustomers';
import maskPhoneNumber from '@lib/utils/maskPhoneNumber';
import getDisplayName from '@lib/utils/getDisplayName';
import getRecordingStatus, {
  GetRecordingStatusOptions,
} from './lib/getRecordingStatus';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import ServiceModule from '@modules/ServiceModule';
import textTaskType from './taskTypes/textTaskType';
import voiceTaskType from './taskTypes/voiceTaskType';
import manualBookingTaskType from './taskTypes/manualBookingTaskType';
import BrowserModule from '@modules/BrowserModule';
import {
  clearManualBookingsWhenGateTurnsOff,
  hasOpenManualBookings,
  startManualBooking,
  startManualBookingFromHome,
  type ManualBookingPrefill,
} from './lib/manualBookingCards';
import ModalsModule from '@modules/ModalsModule';
import { ModalId } from '@modules/ModalsModule/types';
import { PcsConversationDetailsResult } from '@api/queries/getPcsConversationDetails';
import handleCallDetailsFetched from './events/handleCallDetailsFetched';
import { isTenantId } from '@lib/utils/tenant';
import { classifyLiveServicesOriginCallForTask } from './lib/classifyLiveServicesOriginCallForTask';
import AuthModule from '@modules/AuthModule';
import InteractionsModule from '@modules/InteractionsModule';
import { showClassificationWaitToast } from './lib/classificationWaitToast';

export class InboxModule extends AppModule {
  override events = [
    'user.status.initialized',
    'phone.pcs.conversation.conversationdetails.fetched',
  ];
  private userId: string | null = null;
  override preload(): void {
    // register task definitions
    TasksModule.registerTaskDefinition(textTaskType);
    TasksModule.registerTaskDefinition(voiceTaskType);
    // Registered unconditionally: flags are not loaded yet at preload. Manual
    // booking cards are only created behind the Assigned to Me gate.
    TasksModule.registerTaskDefinition(manualBookingTaskType);
    clearManualBookingsWhenGateTurnsOff();
    // a reload clears the cards, so it warns first
    BrowserModule.setOpenManualBookingsCheck(hasOpenManualBookings);
    // FFs load too late to use to decide which task type to register
    // so we'll just not load the V1 task type
    // TasksModule.registerTaskDefinition(voiceV1TaskType);

    // auth handlers
    this.app.events.on(AuthEvents.SIGNIN, (user: AuthUser) => {
      this.userId = user.id;
      resetInboxState();
    });
    this.app.events.on(AuthEvents.SIGNOUT, () => {
      this.userId = null;
      resetInboxState();
    });

    // task handlers
    this.app.events.on('task.action.accept', (task: ITask) =>
      handleTaskAccept(this.context, task),
    );
    this.app.events.on('task.action.reject', (task: ITask) =>
      handleTaskReject(this.context, task),
    );
    this.app.events.on(
      'task.action.complete',
      (task: ITask, options?: TaskActionOptions) => {
        handleTaskComplete(this.context, task, false, options);
      },
    );
    this.app.events.on('task.action.complete-classified', (task: ITask) =>
      handleTaskComplete(this.context, task, true),
    );
    this.app.events.on('task.action.unhold', (task: ITask) =>
      handleTaskUnhold(this.context, task),
    );
    this.app.events.on('task.action.transfer', (task: ITask) => {
      handleTaskTransfer(this.context, task);
    });

    // service session handlers
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.DELIVERED,
      (ev) => handleSessionDelivered(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.REJECTED,
      (ev) => handleSessionRejected(this.context, ev),
    );
    this.app.events.on<ServiceSessionOfferedEvent>(
      ServiceSessionEventType.OFFERED,
      (ev) => {
        handleSessionOffered(this.context, ev);
      },
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.RESCINDED,
      (ev) => handleSessionRescinded(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.HANGUP,
      (ev) => handleSessionHangup(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.RECOVERY_COMPLETED,
      (ev) => handleSessionRecoveryCompleted(this.context, ev),
    );
    this.app.events.on<ServiceSessionRecoveryEvent>(
      ServiceSessionEventType.RECOVERY_STARTED,
      (ev) => handleSessionRecoveryStarted(this.context, ev),
    );
    this.app.events.on<ServiceSessionCreatedEvent>(
      ServiceSessionEventType.CREATED,
      (ev) => handleSessionCreated(this.context, ev),
    );
    this.app.events.on<ServiceSessionEvent>(
      ServiceSessionEventType.DELETED,
      (ev) => handleSessionDeleted(this.context, ev),
    );

    // phone call handlers
    this.app.events.on('phone.call.answered', (ev: CallEvent) =>
      handleCallAnswered(this.context, ev),
    );
    this.app.events.on('phone.call.ended', (ev: CallEvent) => {
      // stop tracking the poll unconditionally, before anything below that
      // could throw (e.g. no active user/task), so it's never skipped
      this.stopPollingRecordingStatus(ev.callId);
      handleCallEnded(this.context, ev);
    });
    this.app.events.on('phone.call.placed', (ev: CallEvent) => {
      handleCallPlaced(this.context, ev);
    });
    this.app.events.on('phone.call.missed', (ev: CallEvent) =>
      handleCallMissed(this.context, ev),
    );

    this.app.events.on('phone.call.failed', (ev: WebphoneCallEvent) =>
      handleCallFailed(this.context, ev),
    );

    // SIP call-failed handler (CCPE-425). Drives inbound queue task transitions
    // by SIP reason code; flag-gated, no-op when the flag is OFF. Not added to
    // the `events` array because that array registers events this module EMITS —
    // here we only listen (same as phone.call.missed above).
    this.app.events.on('phone.call.failed-general', (ev: WebphoneCallEvent) =>
      handleCallFailedGeneral(this.context, ev),
    );

    this.app.events.on(
      'phone.pcs.conversation.conversationdetails.fetched',
      ({
        callId,
        conversationDetails,
      }: {
        callId: string;
        conversationDetails: PcsConversationDetailsResult;
      }) => {
        handleCallDetailsFetched(this.context, callId, conversationDetails);
      },
    );

    this.app.events.on<PhonePrepareOutgoingEvent>(
      'phone.prepare.outbound',
      (eventData) => handleTaskPrepareOutbound(this.context, eventData),
    );

    // user status handlers
    this.app.events.on('userStatus.updated.available', this.resetMissedCalls);

    this.app.events.on('user.status.initialized', () => {
      this.handleInProgressTransferTasksOnLoad();
    });
  }

  /**
   * Goes through all tasks and calls the transfer handler
   * for each task that has an inProgress transfer status.
   *
   * This is used to handle the transfer tasks that were in progress
   * at the time of the app refresh.
   */
  private handleInProgressTransferTasksOnLoad = () => {
    const featureInboxCallWarmTransfers = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureInboxCallWarmTransfers,
    );
    if (!featureInboxCallWarmTransfers) {
      TasksModule.getTasks().forEach((task) => {
        if (task.attributes.transferStatus?.inProgress) {
          handleTaskTransfer(this.context, task);
        }
      });
    }
  };

  public setSelectedTaskId(id: string | undefined) {
    inboxActions.setSelectedTaskId(id);
  }

  /**
   * Freezes selection on the task (`undefined` for the home view) while a
   * Close or leave prompt is open.
   */
  public holdSelection(taskId: string | undefined): number {
    return inboxActions.holdSelection(taskId);
  }

  /** Releases the hold and returns the deferred request for the caller to apply. */
  public releaseSelection(token: number): DeferredSelection | undefined {
    return inboxActions.releaseSelection(token);
  }

  /**
   * Applies a Close prompt's deferred selection: the newest request whose
   * task still exists, falling back to earlier ones.
   */
  public applyDeferredSelection(deferred: DeferredSelection | undefined) {
    applyDeferredSelection(deferred);
  }

  /** A CSR-initiated selection (`undefined` for home): bypasses any hold. */
  public selectTaskByUser(id: string | undefined) {
    inboxActions.selectTaskByUser(id);
  }

  private get context(): InboxContext {
    if (!this.userId) {
      throw new Error('User not signed in');
    }

    return {
      userId: this.userId,
      logger: this.app.logger,
      parent: this,
    };
  }

  public handleMissedCallsForQueue = (queueId: string) => {
    inboxState.missedCallsForQueue[queueId] = inboxState.missedCallsForQueue[
      queueId
    ]
      ? inboxState.missedCallsForQueue[queueId] + 1
      : 1;
  };

  public handleMissedDidCall = () => {
    inboxState.missedCallsDid += 1;
  };

  public resetMissedCallsDid = () => {
    inboxState.missedCallsDid = 0;
  };

  public resetMissedQueueCalls = () => {
    inboxState.missedCallsForQueue = {};
  };

  public resetMissedCalls = () => {
    this.resetMissedQueueCalls();
    this.resetMissedCallsDid();
  };

  public showClassificationModalForTaskId = (taskId: string) => {
    if (taskId) {
      ModalsModule.openModal(ModalId.CallClassification, {
        open: true,
        taskId: taskId,
      });
    } else {
      ModalsModule.closeActiveModal();
    }
  };

  /** A no-op unless the agent claimed the customer's call for this task. */
  public classifyLiveServicesOriginCall = (
    task: ITask,
    classification: ChannelClassificationInput,
  ) =>
    classifyLiveServicesOriginCallForTask(
      task,
      this.app.logger,
      classification,
    );

  public setSessionProperties = (
    taskId: string,
    session: Pick<
      ServiceSession,
      | 'conversationId'
      | 'conversationSid'
      | 'queueId'
      | 'queueName'
      | 'queueEnterTime'
      | 'queueTimeout'
      | 'tenantId'
    >,
    to: string,
  ) => {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    TasksModule.setTaskAttribute(taskId, 'sessionId', session.conversationId);
    TasksModule.setTaskAttribute(taskId, 'sessionSid', session.conversationSid);
    if (session.queueTimeout) {
      TasksModule.setTaskAttribute(
        taskId,
        'queueTimeoutAt',
        session.queueTimeout,
      );
    }
    this.setTenantIdForTask(task, session.tenantId);
    task.queueId = session.queueId;
    task.queueName = session.queueName;
    task.to = to;
  };

  public setTenantIdForTask = (task: Task, tenantId: string) => {
    if (task.tenantId === tenantId) {
      return;
    }

    if (!isTenantId(tenantId)) {
      return;
    }

    // A tenant arriving on a tenant-less task is the first one; a customer the
    // CSR picked in the multi-tenant view before then must not be overwritten.
    const isFirstTenant = !task.tenantId;
    task.tenantId = tenantId;

    // search for the customer, set the customer on the task if we find a single match
    searchCustomers(
      {
        phone: maskPhoneNumber(getDisplayName(task.from, {})),
      },
      tenantId,
    )
      .then((results) => {
        // read here, not before the search: the pick may land while it is in flight
        if (isFirstTenant && this.hasKeptPick(task)) {
          this.recordForKeptPick(
            task,
            results.customers.length === 1 ? results.customers[0] : undefined,
          );
          return;
        }
        if (results.customers.length === 1) {
          const customer = results.customers[0];
          TasksModule.setCustomerOnTask(task.id, {
            id: customer.id,
            name: customer.name,
          });
        }
      })
      .catch((error) => {
        // master keeps the pick when the search fails, and so does the record
        if (isFirstTenant && this.hasKeptPick(task)) {
          this.recordForKeptPick(task, undefined);
        }
        this.app.logger.error('Customer phone search failed', error);
      });
  };

  /**
   * Retention on: the card's customer is one the CSR picked, which carries its
   * own tenant (an org node, or the legacy pick's tenant id). A customer with
   * neither, such as one a Direct Line call from a customer record set, is not
   * a pick and the late tenant's match replaces it, as on master.
   */
  private hasKeptPick(task: Task): boolean {
    return (
      this.isBookingRetentionEnabled() &&
      !!task.attributes.customerId &&
      (!!task.attributes.customerOrgNodeId ||
        !!task.attributes.customerTenantId)
    );
  }

  /**
   * The card keeps the pick. A pick from the call's own tenant is recorded as
   * is; no match is stored, so a later pick from another tenant leaves it on
   * the record, as master keeps the CSR's latest in-tenant pick. A pick from
   * another tenant cannot go on the call tenant's interaction as is, so the
   * record gets what master records: the single phone match, else the pick.
   */
  private recordForKeptPick(
    task: Task,
    match: { id: string; name: string } | undefined,
  ) {
    let write: Promise<void>;
    if (!InteractionsModule.isCustomerFromOtherTenant(task)) {
      write = InteractionsModule.recordCustomer(task);
    } else if (match) {
      write = InteractionsModule.recordLateTenantMatch(task, {
        id: match.id,
        name: match.name,
      });
    } else {
      write = InteractionsModule.recordLateTenantMatch(task, null);
    }
    write.catch((error) =>
      this.app.logger.error('Recording the kept pick failed', error),
    );
  }

  public evaluateAndSetTaskType(taskId: string) {
    const task = TasksModule.getTask(taskId);

    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }

    const hasSession = ServiceModule.hasUserSession(
      task.attributes.sessionSid,
      this.userId ?? '',
    );
    const isExternal =
      task.attributes.traits?.includes(ConversationTrait.External) ?? false;
    const isDirectToUser =
      task.attributes.traits?.includes(ConversationTrait.DirectToUser) ?? false;
    const hasTraitsSet = task.attributes.traits !== undefined;
    const isFromInternalUser = task.from.startsWith('User');

    // When RTCS is down (no session), use task.queueId set from PCS as a fallback
    // queue signal so that queue calls are not misclassified as DID calls.
    // DirectToUser calls are excluded because they bypass the queue entirely.
    const isRtcsDegradedQueueDetection = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureRtcsDegradedQueueDetection,
    );
    const isQueueCall =
      hasSession ||
      (isRtcsDegradedQueueDetection && !!task.queueId && !isDirectToUser);

    const updateRecordInteraction = (value: boolean) => {
      // skip setting recordInteraction if the task is already missed
      if (
        task.state !== TaskState.TimedOut &&
        task.state !== TaskState.Abandoned
      ) {
        TasksModule.setTaskAttribute(taskId, 'recordInteraction', value);
      }
    };

    // Normalize the "is external caller" signal:
    // When traits are set, use the External trait directly.
    // When traits are absent (PCS conversation not yet returned or errored),
    // fall back to !isFromInternalUser.
    const isExternalCaller = hasTraitsSet ? isExternal : !isFromInternalUser;

    if (isQueueCall) {
      if (isExternalCaller) {
        task.type = 'voice-external-queue';
        TasksModule.setTaskAttribute(taskId, 'shouldClassify', true);
        updateRecordInteraction(true);
      } else {
        task.type = 'voice-internal-queue';
        TasksModule.setTaskAttribute(taskId, 'shouldClassify', false);
      }
    } else if (isExternalCaller) {
      // For DID calls, only classify if traits confirm it's not a direct-to-user call.
      // When traits are absent, skip classification conservatively.
      const shouldClassify = hasTraitsSet && !isDirectToUser;
      task.type = 'voice-external-did';
      TasksModule.setTaskAttribute(taskId, 'shouldClassify', shouldClassify);
      if (shouldClassify) {
        updateRecordInteraction(true);
      }
    } else if (hasTraitsSet) {
      // hasTraitsSet=true, isExternalCaller=false, isQueueCall=false → internal DID call.
      // Note: the unresolved case (!hasTraitsSet && !isQueueCall && isFromInternalUser) falls
      // through all three branches unchanged — task.type is not set, matching original behavior.
      task.type = 'voice-internal-did';
      TasksModule.setTaskAttribute(taskId, 'shouldClassify', false);
    }
  }

  public isDid(task: Task): boolean {
    return (
      task.type === 'voice-external-did' || task.type === 'voice-internal-did'
    );
  }

  /** See `lib/isBookingRetentionEnabled`. */
  public isBookingRetentionEnabled(): boolean {
    return isBookingRetentionEnabled();
  }

  /** See `lib/isNewAssignedToMeUiEnabled`. */
  public isNewAssignedToMeUiEnabled(): boolean {
    return isNewAssignedToMeUiEnabled();
  }

  /** See `lib/manualBookingCards`. */
  public startManualBooking(): string {
    return startManualBooking();
  }

  /** See `lib/manualBookingCards`. */
  public startManualBookingFromHome(
    prefill?: ManualBookingPrefill,
  ): string | undefined {
    return startManualBookingFromHome(prefill);
  }

  /**
   * True when an ended call task keeps its card in WrapUp instead of
   * auto-completing: outbound calls, and external DID calls that need no
   * classification. A warm transfer's warm-up call is never retained: it is
   * not a customer call and its interaction is deactivated.
   */
  public shouldRetainEndedCallCard(task: Task | ITask): boolean {
    if (!this.isBookingRetentionEnabled()) {
      return false;
    }
    if (task.attributes.isWarmUpCall) {
      return false;
    }
    return (
      task.direction === 'outbound' ||
      (task.type === 'voice-external-did' && !task.attributes.shouldClassify)
    );
  }

  /**
   * Whether Close on this task can go ahead, asked before any discard prompt.
   * Never completes the task or changes its state. It does inherit the side
   * effects of `checkClassificationRequirement` (loading overlay, completedAt
   * write, metrics). Toasts for NotReady/NotEnded match `setCallToCompleted`;
   * a null result adds none, the check shows its own error toast. When the
   * check ran, `ready` carries the requirement it fetched so completion does
   * not ask again. `isCurrent` lets the caller drop an answer for an attempt
   * it has abandoned: that answer shows no toast and returns `blocked`.
   */
  public canCloseTask = async (
    taskId: string,
    isCurrent: () => boolean = () => true,
  ): Promise<CloseCheck> => {
    const task = TasksModule.getTask(taskId);
    if (!task) {
      return { status: 'blocked' };
    }

    const completesWithoutCheck =
      task.state !== TaskState.WrapUp ||
      task.isOutbound ||
      !task.attributes.shouldClassify ||
      !AuthModule.hasAccessToTenant(task.tenantId);
    if (completesWithoutCheck) {
      return { status: 'ready' };
    }

    const requirement = await TasksModule.checkClassificationRequirement(
      task.id,
      isCurrent,
    );
    if (requirement === null || !isCurrent()) {
      return { status: 'blocked' };
    }
    if (requirement === 'NotReady' || requirement === 'NotEnded') {
      showClassificationWaitToast(requirement);
      return { status: 'blocked' };
    }
    return { status: 'ready', requirement };
  };

  public hasSession(task: Task): boolean {
    return !!task.attributes.sessionId;
  }

  // Only one recording-status poll's result is ever applied per callId. A
  // new trigger (Offered, Accepted, transfer-detected) doesn't start a
  // second competing poll and reconcile which result should "win" - it
  // bumps this generation counter, and any in-flight poll checks its own
  // generation before applying a result, so a newer trigger's result always
  // supersedes an older one's. Note this only supersedes the RESULT, not the
  // poll itself - there's no cancellation, so a superseded getRecordingStatus
  // loop keeps issuing queries in the background until it self-resolves.
  //
  // Generations are minted from a single module-wide counter (never reset)
  // rather than a per-callId counter that restarts at 1 after the map entry
  // is deleted (self-cleanup on conclusion, or stopPollingRecordingStatus).
  // A per-callId counter can reissue a number a still-in-flight older poll
  // already holds, letting it pass the "is this poll still current" check
  // and clobber whatever a third, unrelated poll for the same callId is
  // doing. A globally unique generation makes that collision impossible.
  private nextRecordingStatusPollGeneration = 0;
  private readonly recordingStatusPollGenerationByCallId = new Map<
    string,
    number
  >();

  /**
   * (Re)starts the recording-status poll for a call. Safe to call repeatedly
   * for the same callId (e.g. once at Offered, again at Accepted) - each call
   * supersedes the previous one rather than running alongside it.
   */
  public pollRecordingStatus = (
    kazooCallId?: string,
    callId?: string,
    options?: GetRecordingStatusOptions,
  ) => {
    if (!callId || !kazooCallId) {
      this.app.logger.error('CallId not provided for pollRecordingStatus');
      return;
    }

    const generation = ++this.nextRecordingStatusPollGeneration;
    this.recordingStatusPollGenerationByCallId.set(callId, generation);

    getRecordingStatus(
      kazooCallId,
      options?.attemptTimesMs
        ? {
            ...options,
            // Kazoo takes several seconds to report a transferee's real
            // status, so each scheduled check is applied as it arrives and
            // the indicator corrects itself instead of sitting on the
            // transferer's status until the poll concludes.
            onIntermediateStatus: (intermediateStatus) => {
              if (this.isCurrentRecordingStatusPoll(callId, generation)) {
                SoftPhoneModule.setRecordingState(callId, intermediateStatus);
              }
            },
          }
        : options,
    )
      .then((recordingStatus) => {
        // a newer trigger has since restarted the poll for this call (or the
        // call has already ended) - this (now stale) result is discarded
        if (!this.isCurrentRecordingStatusPoll(callId, generation)) {
          return;
        }

        // this poll has concluded and nothing has superseded it - stop
        // tracking it. This is what actually bounds the map's size: not
        // every call-ending path calls stopPollingRecordingStatus (e.g. a
        // missed or failed outbound call doesn't emit phone.call.ended), so
        // self-cleanup on conclusion is what prevents an unanswered/failed
        // call's entry from lingering forever.
        this.recordingStatusPollGenerationByCallId.delete(callId);

        if (
          !recordingStatus ||
          recordingStatus.settings === CallRecordingSettingsOptions.Unset
        ) {
          this.app.logger.warn(
            'Unable to fetch a Recording for call',
            callId,
            recordingStatus,
          );
          return;
        }
        SoftPhoneModule.setRecordingState(callId, recordingStatus);
      })
      .catch((error) => {
        if (this.isCurrentRecordingStatusPoll(callId, generation)) {
          this.recordingStatusPollGenerationByCallId.delete(callId);
        }
        this.app.logger.warn(
          'Failed to fetch recording status for call',
          callId,
          error,
        );
      });
  };

  private isCurrentRecordingStatusPoll(
    callId: string,
    generation: number,
  ): boolean {
    return (
      this.recordingStatusPollGenerationByCallId.get(callId) === generation
    );
  }

  /** Stops tracking a call's recording-status poll so a late response can no longer apply. */
  private stopPollingRecordingStatus(callId: string) {
    this.recordingStatusPollGenerationByCallId.delete(callId);
  }
}

export default new InboxModule(appManager.events, appManager.logger('Inbox'));
